import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  EmojiPeople as EmojiPeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  Height as HeightIcon,
  MoreHoriz as MoreHorizIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import LayersIcon from '@mui/icons-material/Layers';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { usePacienteDetail } from '../hooks/usePacienteDetail';
import { getGrupoName } from '../model/pacienteConstants';
import PacienteReportsTable from './PacienteReportsTable';
import PacienteEntrevistasTable from './PacienteEntrevistasTable';

const COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
  border: '#dbe7f3',
};

const sectionPaperSx = {
  borderRadius: 4,
  border: `1px solid ${COLORS.border}`,
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.75 }}>
    <Typography variant="body2" sx={{ color: COLORS.labelMuted, fontWeight: 600, minWidth: 160 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: COLORS.textMain, textAlign: 'right' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const getActionIcon = (type) => {
  switch (type) {
    case 'entrevista': return <PersonAddIcon />;
    case 'antropometria': return <HeightIcon />;
    case 'fuerza': return <EmojiPeopleIcon />;
    case 'capacidad':
    case 'encoder': return <FitnessCenterIcon />;
    case 'cuestionarios': return <MoreHorizIcon />;
    default: return <MoreHorizIcon />;
  }
};

export default function PacienteDetail() {
  const { idPaciente } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { paciente, loading, actions, cuestionarios } = usePacienteDetail({ idPaciente });
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Grid container spacing={3}>

      {/* Header / Hero */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            ...sectionPaperSx,
            p: { xs: 2, md: 3 },
            background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: COLORS.primaryDark, letterSpacing: 1.4 }}>
                Historial del paciente
              </Typography>
              {loading ? (
                <Skeleton width={280} height={52} sx={{ mt: 0.5 }} />
              ) : (
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  sx={{ mt: 0.5, fontWeight: 600, color: COLORS.textMain, lineHeight: 1.15, wordBreak: 'break-word' }}
                >
                  {paciente?.firstname} {paciente?.lastname}
                </Typography>
              )}
              {!loading && paciente && (
                <Typography sx={{ mt: 1, color: COLORS.textMuted, maxWidth: 680 }}>
                  Accede rápidamente a las evaluaciones y consulta los informes clínicos y entrevistas registradas.
                </Typography>
              )}
            </Box>
            {!loading && paciente && (
              <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' }, flexShrink: 0 }}>
                <IconButton
                  color="primary"
                  onClick={() => setOpen(true)}
                  sx={{ border: `1px solid ${COLORS.border}`, backgroundColor: '#ffffff', borderRadius: 2 }}
                >
                  <VisibilityIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  onClick={() => navigate(`/editar-paciente/${paciente.id}`)}
                  sx={{ border: `1px solid ${COLORS.border}`, backgroundColor: '#ffffff', borderRadius: 2 }}
                >
                  <EditIcon />
                </IconButton>
              </Stack>
            )}
          </Stack>

          {/* Action buttons */}
          <Grid container spacing={2}>
            {actions.map(({ label, icon, path }) => (
              <Grid item xs={12} sm={6} lg={4} key={path}>
                {label === 'Cuestionarios' ? (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      startIcon={getActionIcon(icon)}
                      disabled={loading || !paciente}
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      sx={{
                        py: 1.5,
                        borderRadius: 2.5,
                        justifyContent: 'flex-start',
                        boxShadow: '0 8px 18px rgba(13, 106, 238, 0.2)',
                      }}
                    >
                      {label}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      PaperProps={{
                        sx: { borderRadius: 3, border: `1px solid ${COLORS.border}`, boxShadow: '0 12px 28px rgba(15,23,42,0.1)' },
                      }}
                    >
                      {cuestionarios.map((item) => (
                        <MenuItem
                          key={item.path}
                          onClick={() => { navigate(`/${item.path}/${paciente.id}`); setAnchorEl(null); }}
                        >
                          <ListItemIcon><LayersIcon sx={{ color: COLORS.primary }} /></ListItemIcon>
                          <ListItemText primary={<Typography sx={{ color: COLORS.textMain }}>{item.label}</Typography>} />
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    startIcon={getActionIcon(icon)}
                    disabled={loading || !paciente}
                    onClick={() => navigate(`/${path}/${paciente.id}`)}
                    sx={{
                      py: 1.5,
                      borderRadius: 2.5,
                      justifyContent: 'flex-start',
                      boxShadow: '0 8px 18px rgba(13, 106, 238, 0.2)',
                    }}
                  >
                    {label}
                  </Button>
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      {/* Reports table */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ ...sectionPaperSx, p: { xs: 1.5, sm: 2 } }}>
          <PacienteReportsTable idPaciente={idPaciente} />
        </Paper>
      </Grid>

      {/* Entrevistas table */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ ...sectionPaperSx, p: { xs: 1.5, sm: 2 } }}>
          <PacienteEntrevistasTable idPaciente={idPaciente} />
        </Paper>
      </Grid>

      {/* Patient info modal */}
      <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="paciente-modal-title">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(520px, calc(100% - 32px))',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
            p: 0,
          }}
        >
          {/* Modal header */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
              borderBottom: `1px solid ${COLORS.border}`,
              borderRadius: '16px 16px 0 0',
            }}
          >
            <Typography variant="overline" sx={{ color: COLORS.primaryDark, letterSpacing: 1.4 }}>
              Ficha del paciente
            </Typography>
            <Typography id="paciente-modal-title" variant="h5" sx={{ color: COLORS.textMain, fontWeight: 600, mt: 0.5 }}>
              {paciente?.firstname} {paciente?.lastname}
            </Typography>
          </Box>

          {/* Modal body */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={0} divider={<Divider sx={{ borderColor: COLORS.border }} />}>
              <InfoRow label="ID" value={paciente?.id} />
              <InfoRow label="Código histórico" value={paciente?.codHistorico} />
              <InfoRow label="Teléfono" value={paciente?.telefono} />
              <InfoRow label="Fecha de nacimiento" value={paciente?.dateBirth ? new Date(paciente.dateBirth).toLocaleDateString() : ''} />
              <InfoRow label="Sexo" value={paciente?.sexo === 1 ? 'Masculino' : 'Femenino'} />
              <InfoRow label="Convivencia" value={paciente?.viven} />
              <InfoRow label="Grupo" value={getGrupoName(paciente?.grupo)} />
              <InfoRow label="Nivel educacional" value={paciente?.nivelEducacional} />
              <InfoRow label="Activo" value={paciente?.active ? 'Sí' : 'No'} />
              <InfoRow label="Creado" value={paciente?.createdAt ? new Date(paciente.createdAt).toLocaleDateString() : ''} />
              <InfoRow label="Actualizado" value={paciente?.updatedAt ? new Date(paciente.updatedAt).toLocaleDateString() : ''} />
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={() => navigate(`/editar-paciente/${paciente?.id}`)}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{ borderRadius: 2.5 }}
              >
                Editar
              </Button>
              <Button onClick={() => setOpen(false)} variant="contained" color="primary" sx={{ borderRadius: 2.5 }}>
                Cerrar
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
}
